
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardFooter } from "@/components/ui/card";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "@/components/layout/LanguageSwitcher";
import { cn } from "@/lib/utils";
import { LoginForm } from "@/components/auth/LoginForm";
import { SignupForm } from "@/components/auth/SignupForm";
import { useAuthForm } from "@/hooks/auth/useAuthForm";

interface AuthPageProps {
  initialMode?: "login" | "signup";
}

const AuthPage = ({
  initialMode = "login"
}: AuthPageProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const authForm = useAuthForm(initialMode);
  
  const {
    isLoading,
    username, setUsername,
    email, setEmail,
    password, setPassword,
    name, setName,
    isSignUp, setIsSignUp,
    step,
    avatars,
    selectedAvatar, setSelectedAvatar,
    loginIdentifier, setLoginIdentifier,
    customAvatarPreview,
    avatarTab, setAvatarTab,
    usernameStatus,
    bankName, setBankName,
    branchName, setBranchName,
    accountType, setAccountType,
    accountNumber, setAccountNumber,
    accountHolderName, setAccountHolderName,
    paypayUsername, setPaypayUsername,
    paymentTab, setPaymentTab,
    fileInputRef,
    
    handleNextStep,
    handlePrevStep,
    handleFileChange,
    clearCustomAvatar,
    handleAuth,
    handleSkipPaymentSetup
  } = authForm;

  const handleBackToLanding = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    navigate("/");
  };

  const getProgressPercentage = () => {
    if (!isSignUp) return 100;
    return step / 3 * 100;
  };

  return (
    <div className="min-h-screen w-full flex flex-col items-center justify-center p-4 relative overflow-hidden 
                  bg-gradient-to-br from-gray-50 via-purple-50 to-gray-100 
                  dark:from-gray-900 dark:via-purple-900/10 dark:to-gray-800">
      <Button variant="ghost" size="icon" type="button" onClick={handleBackToLanding} className="absolute top-4 left-4 z-50 h-10 w-10 rounded-full bg-white/80 dark:bg-gray-900/80 
                 hover:bg-white dark:hover:bg-gray-900 backdrop-blur-sm border border-primary/20
                 transition-all duration-200 hover:scale-105">
        <ArrowLeft className="h-5 w-5 text-primary" />
      </Button>

      <div className="absolute top-4 right-4 z-50">
        <LanguageSwitcher className="h-10 w-10 rounded-full bg-white/80 dark:bg-gray-900/80 
                            hover:bg-white dark:hover:bg-gray-900 backdrop-blur-sm border border-primary/20
                            transition-all duration-200 hover:scale-105" />
      </div>

      <div className="absolute inset-0 w-full h-full">
        <div className="absolute top-0 -left-4 w-72 h-72 bg-primary/20 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
        <div className="absolute top-0 -right-4 w-72 h-72 bg-purple-300/20 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-8 left-20 w-72 h-72 bg-primary/30 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000"></div>
      </div>

      <Card className="w-full max-w-md mx-auto relative bg-white/80 dark:bg-gray-900/80 backdrop-blur-md 
                    border border-primary/20 shadow-2xl animate-fade-in">
        <CardHeader className="space-y-1">
          <div className="flex items-center justify-center mb-4">
            <img src="https://tkrrgpbntaqiupgcayjm.supabase.co/storage/v1/object/public/Logo//SakuWari_logo_transparent_black.png" alt="SakuWari Logo" className="h-12 dark:invert" />
          </div>
          <h1 className="text-2xl font-bold text-center bg-gradient-to-r from-primary to-purple-600 bg-clip-text text-transparent">
            {isSignUp && step === 1 ? t('auth.createAccount') : isSignUp ? '' : t('auth.welcomeBack')}
          </h1>
          {isSignUp && (
            <div className="flex justify-center space-x-2 mt-1">
              {[1, 2, 3].map(dot => (
                <div 
                  key={dot} 
                  className={cn(
                    "h-2 w-2 rounded-full transition-all duration-300 ease-spring", 
                    step === dot ? "bg-primary w-8" : "bg-primary/20"
                  )} 
                />
              ))}
            </div>
          )}
        </CardHeader>

        <CardContent>
          {isSignUp ? (
            <SignupForm
              step={step}
              isLoading={isLoading}
              name={name}
              setName={setName}
              email={email}
              setEmail={setEmail}
              username={username}
              setUsername={setUsername}
              password={password}
              setPassword={setPassword}
              // Type narrowing to satisfy the expected type
              usernameStatus={usernameStatus === "" ? "typing" : usernameStatus}
              avatars={avatars}
              selectedAvatar={selectedAvatar}
              setSelectedAvatar={setSelectedAvatar}
              avatarTab={avatarTab}
              setAvatarTab={setAvatarTab}
              customAvatarPreview={customAvatarPreview}
              clearCustomAvatar={clearCustomAvatar}
              fileInputRef={fileInputRef}
              handleFileChange={handleFileChange}
              paymentTab={paymentTab}
              setPaymentTab={setPaymentTab}
              bankName={bankName}
              setBankName={setBankName}
              branchName={branchName}
              setBranchName={setBranchName}
              accountType={accountType}
              setAccountType={setAccountType}
              accountNumber={accountNumber}
              setAccountNumber={setAccountNumber}
              accountHolderName={accountHolderName}
              setAccountHolderName={setAccountHolderName}
              paypayUsername={paypayUsername}
              setPaypayUsername={setPaypayUsername}
              onSubmit={handleAuth}
              handlePrevStep={handlePrevStep}
              // Fix: Pass the function directly without wrapping it
              handleSkipPaymentSetup={handleSkipPaymentSetup}
              switchToLogin={() => setIsSignUp(false)}
            />
          ) : (
            <LoginForm
              loginIdentifier={loginIdentifier}
              setLoginIdentifier={setLoginIdentifier}
              password={password}
              setPassword={setPassword}
              isLoading={isLoading}
              onSubmit={handleAuth}
              switchToSignUp={() => setIsSignUp(true)}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AuthPage;
